import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { API, formatApiErrorDetail, fileUrl, uploadFile } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { StatusBadge, assetTypeLabel, euro, fmtDate, fmtDateTime, nowRomeInput, trackingUnit, EmptyState } from "@/components/shared";
import SignaturePad from "@/components/SignaturePad";
import { FileUploadField } from "@/components/PhotoUpload";
import NotificationBell from "@/components/NotificationBell";
import {
  ArrowLeft, Wrench, Plus, Trash2, ClipboardSignature, FileDown,
  RotateCcw, User, Gauge, Calendar, Paperclip, CalendarClock, ImagePlus, Loader2, X, Camera, Upload, FileCheck2,
  ChevronLeft, ChevronRight, Pencil,
} from "lucide-react";

const emptyM = { date: new Date().toISOString().slice(0, 10), activity_type_id: "", km_hours: 0, activities: "", supplier_id: "", supplier: "", invoice_number: "", invoice_date: "", invoice_amount: 0, next_due_date: "", next_due_km_hours: 0, notes: "", invoice_path: "", invoice_filename: "" };

export default function AssetDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [asset, setAsset] = useState(null);
  const [maint, setMaint] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [activityTypes, setActivityTypes] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [mOpen, setMOpen] = useState(false);
  const [mForm, setMForm] = useState(emptyM);
  const [editMId, setEditMId] = useState(null);
  const [maintTypeFilter, setMaintTypeFilter] = useState("all");
  const [aOpen, setAOpen] = useState(false);
  const [assignUser, setAssignUser] = useState("");
  const [skipSig, setSkipSig] = useState(false);
  const [assignNotes, setAssignNotes] = useState("");
  const [assignDate, setAssignDate] = useState("");
  const [assignQty, setAssignQty] = useState(1);
  const [busy, setBusy] = useState(false);
  const sigRef = useRef(null);
  const [rOpen, setROpen] = useState(false);
  const [operatorName, setOperatorName] = useState(user?.name || "");
  const [history, setHistory] = useState([]);
  const [damagePhotos, setDamagePhotos] = useState([]);
  const [photoBusy, setPhotoBusy] = useState(false);
  const [returnDate, setReturnDate] = useState("");
  const [skipReturnSig, setSkipReturnSig] = useState(false);
  const [returnSignedPath, setReturnSignedPath] = useState("");
  const [returnSignedName, setReturnSignedName] = useState("");
  const [returnDocBusy, setReturnDocBusy] = useState(false);
  const returnDocInputRef = useRef(null);
  const [lightbox, setLightbox] = useState(null);
  const damageInputRef = useRef(null);
  const opSigRef = useRef(null);
  const empSigRef = useRef(null);

  const addDamagePhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Seleziona un'immagine"); return; }
    setPhotoBusy(true);
    try { const res = await uploadFile(file); setDamagePhotos((p) => [...p, res.path]); }
    catch { toast.error("Caricamento foto fallito"); }
    finally { setPhotoBusy(false); if (damageInputRef.current) damageInputRef.current.value = ""; }
  };

  const load = () => {
    api.get(`/assets/${id}`).then(({ data }) => setAsset(data)).catch(() => navigate("/beni"));
    api.get(`/assets/${id}/maintenance`).then(({ data }) => setMaint(data));
    api.get(`/assets/${id}/assignments`).then(({ data }) => setHistory(data)).catch(() => {});
    api.get("/users").then(({ data }) => setEmployees(data.filter((u) => u.role === "employee" || u.role === "admin")));
    api.get("/activity-types").then(({ data }) => setActivityTypes(data)).catch(() => {});
    api.get("/suppliers").then(({ data }) => setSuppliers(data)).catch(() => {});
  };
  useEffect(load, [id]);

  const damageGroupsData = (() => {
    let gi = 0;
    const groups = history.filter((h) => (h.damage_photos || []).length > 0).map((h) => ({
      ...h, photos: h.damage_photos.map((p) => ({ path: p, gi: gi++ })),
    }));
    return { groups, all: groups.flatMap((g) => g.photos) };
  })();
  const damageGroups = damageGroupsData.groups;
  const allDamage = damageGroupsData.all;

  useEffect(() => {
    if (lightbox === null) return;
    const onKey = (e) => {
      if (e.key === "Escape") setLightbox(null);
      else if (e.key === "ArrowRight") setLightbox((i) => (i + 1) % allDamage.length);
      else if (e.key === "ArrowLeft") setLightbox((i) => (i - 1 + allDamage.length) % allDamage.length);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightbox, allDamage.length]);

  if (!asset) return null;
  const unit = trackingUnit(asset.tracking_type);

  // latest deadline per activity type
  const deadlines = Object.values(
    maint.filter((m) => m.next_due_date).reduce((acc, m) => {
      const k = m.activity_type_name || "Manutenzione";
      if (!acc[k] || m.date > acc[k].date) acc[k] = m;
      return acc;
    }, {})
  ).map((m) => {
    const due = new Date(m.next_due_date);
    const days = Math.ceil((due - new Date()) / 86400000);
    return { ...m, type: m.activity_type_name || "Manutenzione", days };
  }).sort((a, b) => a.days - b.days);

  const saveMaint = async () => {
    if (!mForm.activities) { toast.error("Descrivi le attività effettuate"); return; }
    setBusy(true);
    const payload = {
      ...mForm,
      km_hours: Number(mForm.km_hours) || 0,
      invoice_amount: Number(mForm.invoice_amount) || 0,
      next_due_km_hours: Number(mForm.next_due_km_hours) || 0,
    };
    try {
      if (editMId) await api.put(`/maintenance/${editMId}`, payload);
      else await api.post(`/assets/${id}/maintenance`, payload);
      toast.success(editMId ? "Attività aggiornata" : "Attività registrata");
      setMOpen(false); setMForm(emptyM); setEditMId(null); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const openNewMaint = () => { setMForm(emptyM); setEditMId(null); setMOpen(true); };
  const openEditMaint = (m) => { setMForm({ ...emptyM, ...m }); setEditMId(m.id); setMOpen(true); };

  const maintTypes = [...new Set(maint.map((m) => m.activity_type_name).filter(Boolean))].sort();
  const shownMaint = maintTypeFilter === "all" ? maint : maint.filter((m) => (m.activity_type_name || "") === maintTypeFilter);

  const delMaint = async (rid) => {
    if (!window.confirm("Eliminare questa registrazione?")) return;
    await api.delete(`/maintenance/${rid}`);
    toast.success("Registrazione eliminata"); load();
  };

  const assign = async () => {
    if (!assignUser) { toast.error("Seleziona un dipendente"); return; }
    if (!skipSig && sigRef.current?.isEmpty()) { toast.error("È richiesta la firma per la presa in carico, oppure attiva \"Salta firma\""); return; }
    if (!assignDate) { toast.error("Indica la data di consegna"); return; }
    const isQty = asset?.management_type === "quantita";
    const q = Math.max(parseInt(assignQty, 10) || 1, 1);
    if (isQty && q > (asset.available_qty || 0)) { toast.error(`Disponibili ${asset.available_qty}, richiesti ${q}`); return; }
    setBusy(true);
    try {
      await api.post("/assignments", {
        asset_ids: [id], user_id: assignUser,
        signature: skipSig ? "" : sigRef.current.getData(), notes: assignNotes,
        assigned_date: `${assignDate}:00`,
        quantities: isQty ? { [id]: q } : undefined,
      });
      toast.success(skipSig ? "Bene assegnato senza firma" : "Bene assegnato e firmato");
      setAOpen(false); setAssignUser(""); setAssignNotes(""); setSkipSig(false); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const handleReturnSignedDoc = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.type !== "application/pdf") { toast.error("Seleziona un file PDF"); return; }
    setReturnDocBusy(true);
    try {
      const up = await uploadFile(file);
      setReturnSignedPath(up.path); setReturnSignedName(file.name);
      toast.success("PDF firmato caricato");
    } catch { toast.error("Caricamento fallito"); }
    finally { setReturnDocBusy(false); }
  };

  const doReturn = async () => {
    if (!operatorName.trim()) { toast.error("Indica il nome di chi effettua l'operazione"); return; }
    if (!returnDate) { toast.error("Indica la data di restituzione"); return; }
    const hasSignedDoc = !!returnSignedPath;
    const needSig = !skipReturnSig && !hasSignedDoc;
    if (needSig && (opSigRef.current?.isEmpty() || empSigRef.current?.isEmpty())) {
      toast.error("Entrambe le firme sono richieste, oppure attiva \"Salta firma\" o carica il PDF firmato");
      return;
    }
    setBusy(true);
    try {
      await api.post(`/assignments/${asset.assignment_id}/return`, {
        operator_name: operatorName,
        signature_operator: needSig ? opSigRef.current.getData() : "",
        signature_employee: needSig ? empSigRef.current.getData() : "",
        damage_photos: damagePhotos,
        asset_ids: [asset.id],
        returned_date: `${returnDate}:00`,
        return_signed_pdf_path: returnSignedPath,
        return_signed_pdf_filename: returnSignedName,
        return_signed_by: hasSignedDoc ? `${asset.assigned_to || ""}${operatorName ? " e " + operatorName : ""}`.trim() : "",
        return_skipped: skipReturnSig,
      });
      toast.success("Bene restituito e verbale registrato");
      setROpen(false); setOperatorName(user?.name || ""); setDamagePhotos([]); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const info = [
    { label: "Categoria", value: asset.category_name },
    { label: "Tipologia", value: assetTypeLabel(asset.asset_type) },
    { label: "Marca / Modello", value: `${asset.brand || ""} ${asset.model || ""}`.trim() || "—" },
    { label: "Matricola / Targa", value: asset.identifier || "—" },
    { label: "Anno", value: asset.year || "—" },
    { label: `Lettura (${unit || "n/d"})`, value: asset.tracking_type !== "nessuno" ? `${asset.current_value || 0} ${unit}${asset.reading_date ? ` · rilevata il ${fmtDate(asset.reading_date)}` : ""}` : "—" },
  ];

  return (
    <div>
      <Button variant="ghost" onClick={() => navigate("/beni")} className="mb-4 -ml-2" data-testid="back-button">
        <ArrowLeft className="h-4 w-4 mr-1.5" /> Torna ai beni
      </Button>

      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-6">
        <div>
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="font-heading font-black text-3xl sm:text-4xl tracking-tight">{asset.name}</h1>
            <StatusBadge status={asset.status} />
          </div>
          <p className="text-muted-foreground mt-1">{asset.brand} {asset.model} {asset.identifier && `· ${asset.identifier}`}</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {isAdmin && (asset.assigned_to_id ? (
            <Button variant="outline" onClick={() => { setOperatorName(user?.name || ""); setDamagePhotos([]); setReturnDate(nowRomeInput()); setSkipReturnSig(false); setReturnSignedPath(""); setReturnSignedName(""); setROpen(true); }} data-testid="return-asset-button">
              <RotateCcw className="h-4 w-4 mr-1.5" /> Restituisci
            </Button>
          ) : (
            <Button onClick={() => { setSkipSig(false); setAssignDate(nowRomeInput()); setAssignQty(1); setAOpen(true); }} data-testid="assign-asset-button" disabled={asset.status === "dismesso" || (asset.management_type === "quantita" && (asset.available_qty || 0) <= 0)}>
              <ClipboardSignature className="h-4 w-4 mr-1.5" /> Assegna e firma
            </Button>
          ))}
          <div className="hidden lg:block"><NotificationBell /></div>
        </div>
      </div>

      {deadlines.length > 0 && (
        <Card className="p-6 rounded-lg mb-6">
          <div className="flex items-center gap-2 mb-4">
            <CalendarClock className="h-4 w-4 text-primary" />
            <div className="label-caps text-muted-foreground">Scadenze per attività</div>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {deadlines.map((d) => {
              const st = d.days < 0 ? "scaduta" : d.days <= 30 ? "in_scadenza" : "ok";
              const cls = st === "scaduta" ? "border-destructive/40 bg-destructive/5" : st === "in_scadenza" ? "border-warning/40 bg-warning/5" : "border-border";
              const txt = st === "scaduta" ? "text-destructive" : st === "in_scadenza" ? "text-warning" : "text-success";
              return (
                <div key={d.type} data-testid={`deadline-${d.type}`} className={`p-3 rounded-md border ${cls}`}>
                  <div className="label-caps text-muted-foreground">{d.type}</div>
                  <div className="font-heading font-bold text-lg mt-1">{fmtDate(d.next_due_date)}</div>
                  <div className={`text-xs font-semibold ${txt}`}>
                    {d.days < 0 ? `Scaduta da ${Math.abs(d.days)}g` : d.days === 0 ? "Scade oggi" : `Tra ${d.days}g`}
                  </div>
                </div>
              );
            })}
          </div>
        </Card>
      )}

      <div className="grid lg:grid-cols-3 gap-4 mb-6">
        <Card className="p-6 rounded-lg lg:col-span-2">
          <div className="label-caps text-muted-foreground mb-4">Scheda tecnica</div>
          {asset.image && (
            <img src={fileUrl(asset.image)} alt={asset.name} className="w-full h-48 object-cover rounded-md border border-border mb-5" data-testid="asset-photo" />
          )}
          <div className="grid sm:grid-cols-2 gap-y-4 gap-x-8">
            {info.map((i) => (
              <div key={i.label} className="flex justify-between border-b border-border/60 pb-2">
                <span className="text-sm text-muted-foreground">{i.label}</span>
                <span className="text-sm font-semibold text-right">{i.value}</span>
              </div>
            ))}
          </div>
          {asset.notes && <p className="mt-4 text-sm text-muted-foreground">{asset.notes}</p>}
        </Card>

        <Card className="p-6 rounded-lg">
          <div className="label-caps text-muted-foreground mb-4">Assegnazione attuale</div>
          {asset.management_type === "quantita" ? (
            <div className="space-y-3" data-testid="asset-detail-quantity">
              <div className="flex items-center justify-between rounded-md bg-primary/10 text-primary px-3 py-2">
                <span className="text-sm font-semibold">Disponibili</span>
                <span className="text-sm font-bold tabular-nums" data-testid="asset-detail-available">{asset.available_qty}/{asset.quantity}</span>
              </div>
              {(asset.holders || []).length ? (
                <div className="space-y-1.5">
                  {asset.holders.map((h) => (
                    <div key={h.assignment_id} className="flex items-center gap-3" data-testid={`asset-holder-${h.assignment_id}`}>
                      <div className="h-9 w-9 rounded-md bg-primary/10 flex items-center justify-center shrink-0">
                        <User className="h-4 w-4 text-primary" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-sm truncate">{h.employee_name}</div>
                        <div className="text-xs text-muted-foreground">In dotazione</div>
                      </div>
                      <span className="text-sm font-bold tabular-nums">× {h.qty}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-sm text-muted-foreground text-center py-3">Nessuna assegnazione attiva</div>
              )}
            </div>
          ) : asset.assigned_to ? (
            <div className="flex items-center gap-3">
              <div className="h-11 w-11 rounded-md bg-primary/10 flex items-center justify-center">
                <User className="h-5 w-5 text-primary" />
              </div>
              <div>
                <div className="font-semibold">{asset.assigned_to}</div>
                <div className="text-xs text-muted-foreground">In dotazione</div>
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center py-6 text-muted-foreground text-sm">
              <User className="h-8 w-8 text-muted-foreground/40 mb-2" />
              Non assegnato
            </div>
          )}
        </Card>
      </div>

      <Card className="p-6 rounded-lg mb-6">
        <div className="label-caps text-muted-foreground mb-4">Storico assegnazioni e riconsegne</div>
        {history.length ? (
          <div className="space-y-2">
            {history.map((h) => (
              <div key={h.id} data-testid={`history-${h.id}`} className="flex items-center justify-between p-3 rounded-md border border-border">
                <div className="flex items-center gap-3">
                  <div className={`h-9 w-9 rounded-md flex items-center justify-center ${h.status === "attivo" ? "bg-success/12 text-success" : "bg-muted text-muted-foreground"}`}>
                    <User className="h-4 w-4" />
                  </div>
                  <div>
                    <div className="font-semibold">{h.employee_name}</div>
                    <div className="text-xs text-muted-foreground">
                      Consegna {fmtDateTime(h.assigned_date)}{h.assigned_by ? ` · da ${h.assigned_by}` : ""}
                      {h.returned_date ? ` — Riconsegna ${fmtDateTime(h.returned_date)}${h.return_operator_name ? ` · a ${h.return_operator_name}` : ""}` : ""}
                    </div>
                  </div>
                </div>
                <StatusBadge status={h.status} />
              </div>
            ))}
          </div>
        ) : (
          <div className="text-sm text-muted-foreground py-4 text-center">Nessuna assegnazione registrata per questo bene.</div>
        )}
      </Card>

      {history.some((h) => (h.damage_photos || []).length > 0) && (
        <Card className="p-6 rounded-lg mb-6" data-testid="damage-history-card">
          <div className="flex items-center gap-2 mb-4">
            <Camera className="h-4 w-4 text-primary" />
            <div className="label-caps text-muted-foreground">Storico foto danni</div>
          </div>
          <div className="space-y-5">
            {damageGroups.map((h) => (
              <div key={h.id} data-testid={`damage-group-${h.id}`}>
                <div className="text-sm font-semibold mb-2">
                  Riconsegna {h.returned_date ? fmtDateTime(h.returned_date) : ""}
                  <span className="text-muted-foreground font-normal"> · {h.employee_name}{h.return_operator_name ? ` → ${h.return_operator_name}` : ""}</span>
                </div>
                <div className="flex flex-wrap gap-3">
                  {h.photos.map((ph, i) => (
                    <button key={i} type="button" onClick={() => setLightbox(ph.gi)} data-testid={`damage-photo-${h.id}-${i}`}
                      className="h-24 w-24 rounded-md border border-border overflow-hidden hover:ring-2 hover:ring-primary transition-all">
                      <img src={fileUrl(ph.path)} alt={`Danno ${i + 1}`} className="h-full w-full object-cover" />
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      <Tabs defaultValue="maint">
        <TabsList>
          <TabsTrigger value="maint" data-testid="tab-maintenance">Attività & Scadenze ({maint.length})</TabsTrigger>
        </TabsList>
        <TabsContent value="maint" className="mt-4">
          <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
            <div className="flex items-center gap-4 flex-wrap">
              <div className="flex items-center gap-2 text-sm" data-testid="maint-total">
                <span className="label-caps text-muted-foreground">Totale attività</span>
                <span className="stat-number text-lg">{euro(maint.reduce((s, m) => s + (m.invoice_amount || 0), 0))}</span>
              </div>
              {maintTypes.length > 0 && (
                <Select value={maintTypeFilter} onValueChange={setMaintTypeFilter}>
                  <SelectTrigger className="w-52 h-9" data-testid="maint-type-filter"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tutti i tipi</SelectItem>
                    {maintTypes.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              )}
            </div>
            {isAdmin && (
              <Button onClick={openNewMaint} data-testid="add-maintenance-button">
                <Plus className="h-4 w-4 mr-1.5" /> Registra attività
              </Button>
            )}
          </div>
          {maint.length ? (
            shownMaint.length ? (
            <div className="space-y-3">
              {shownMaint.map((m) => (
                <Card key={m.id} data-testid={`maint-${m.id}`} className="p-5 rounded-lg">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 flex-wrap mb-2">
                        {m.activity_type_name && (
                          <Badge variant="outline" className="rounded-md font-semibold bg-primary/10 text-primary border-primary/25">{m.activity_type_name}</Badge>
                        )}
                        <span className="flex items-center gap-1.5 text-sm font-semibold"><Calendar className="h-4 w-4 text-primary" />{fmtDate(m.date)}</span>
                        {asset.tracking_type !== "nessuno" && (
                          <span className="flex items-center gap-1.5 text-sm text-muted-foreground"><Gauge className="h-4 w-4" />{m.km_hours} {unit}</span>
                        )}
                        {m.supplier && <span className="text-sm text-muted-foreground">· {m.supplier}</span>}
                      </div>
                      <p className="text-sm">{m.activities}</p>
                      {m.notes && <p className="text-xs text-muted-foreground mt-1">{m.notes}</p>}
                      {m.next_due_date && (
                        <div className="mt-2 label-caps text-warning">Prossima scadenza: {fmtDate(m.next_due_date)}{m.next_due_km_hours ? ` · ${m.next_due_km_hours} ${unit}` : ""}</div>
                      )}
                      {m.invoice_path && (
                        <a href={fileUrl(m.invoice_path)} target="_blank" rel="noreferrer" data-testid={`invoice-${m.id}`}
                           className="inline-flex items-center gap-1.5 mt-2 text-sm text-primary hover:underline font-medium">
                          <Paperclip className="h-4 w-4" /> {m.invoice_filename || "Fattura PDF"}
                        </a>
                      )}
                    </div>
                    <div className="text-right shrink-0">
                      <div className="stat-number text-xl">{euro(m.invoice_amount)}</div>
                      {(m.invoice_number || m.invoice_date) && (
                        <div className="text-xs text-muted-foreground mt-0.5">
                          {m.invoice_number ? `Fatt. ${m.invoice_number}` : ""}{m.invoice_number && m.invoice_date ? " · " : ""}{m.invoice_date ? fmtDate(m.invoice_date) : ""}
                        </div>
                      )}
                      {isAdmin && (
                        <div className="mt-1">
                          <Button variant="ghost" size="icon" onClick={() => openEditMaint(m)} data-testid={`edit-maint-${m.id}`}>
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="icon" onClick={() => delMaint(m.id)} data-testid={`del-maint-${m.id}`}>
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                </Card>
              ))}
            </div>
            ) : (
              <Card className="rounded-lg">
                <EmptyState icon={Wrench} title="Nessuna attività per questo tipo" subtitle="Modifica il filtro per vedere le altre attività registrate." />
              </Card>
            )
          ) : (
            <Card className="rounded-lg">
              <EmptyState icon={Wrench} title="Nessuna attività registrata" subtitle="Registra interventi e scadenze (revisione, assicurazione, manutenzione…) con km/ore, fornitore e importo." />
            </Card>
          )}
        </TabsContent>
      </Tabs>

      {/* Maintenance dialog */}
      <Dialog open={mOpen} onOpenChange={setMOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">{editMId ? "Modifica attività" : "Registra attività"}</DialogTitle>
            <DialogDescription>Registra un intervento o una scadenza per questo bene.</DialogDescription>
          </DialogHeader>
          <div className="grid sm:grid-cols-2 gap-4 py-2">
            <div className="space-y-1.5 sm:col-span-2">
              <Label>Tipo di attività</Label>
              <Select value={mForm.activity_type_id} onValueChange={(v) => setMForm({ ...mForm, activity_type_id: v })}>
                <SelectTrigger data-testid="maint-type-select"><SelectValue placeholder="Seleziona tipo (es. Revisione)" /></SelectTrigger>
                <SelectContent>
                  {activityTypes.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Data intervento</Label>
              <Input type="date" data-testid="maint-date-input" value={mForm.date} onChange={(e) => setMForm({ ...mForm, date: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label>Lettura {unit || "km/ore"}</Label>
              <Input type="number" data-testid="maint-km-input" value={mForm.km_hours} onChange={(e) => setMForm({ ...mForm, km_hours: e.target.value })} disabled={asset.tracking_type === "nessuno"} />
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <Label>Attività effettuate *</Label>
              <Textarea data-testid="maint-activities-input" value={mForm.activities} onChange={(e) => setMForm({ ...mForm, activities: e.target.value })} rows={2} placeholder="Es. Tagliando, cambio olio e filtri" />
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <Label>Fornitore</Label>
              <Select value={mForm.supplier_id} onValueChange={(v) => setMForm({ ...mForm, supplier_id: v, supplier: suppliers.find((s) => s.id === v)?.name || "" })}>
                <SelectTrigger data-testid="maint-supplier-select"><SelectValue placeholder="Seleziona un fornitore" /></SelectTrigger>
                <SelectContent>
                  {suppliers.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                </SelectContent>
              </Select>
              {!suppliers.length && <p className="text-xs text-muted-foreground">Aggiungi fornitori in Anagrafiche › Fornitori.</p>}
            </div>
            <div className="space-y-1.5">
              <Label>Numero fattura</Label>
              <Input data-testid="maint-invoice-number-input" value={mForm.invoice_number} onChange={(e) => setMForm({ ...mForm, invoice_number: e.target.value })} placeholder="Es. 2024/128" />
            </div>
            <div className="space-y-1.5">
              <Label>Data fattura</Label>
              <Input type="date" data-testid="maint-invoice-date-input" value={mForm.invoice_date} onChange={(e) => setMForm({ ...mForm, invoice_date: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label>Importo fattura (€)</Label>
              <Input type="number" data-testid="maint-amount-input" value={mForm.invoice_amount} onChange={(e) => setMForm({ ...mForm, invoice_amount: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label>Prossima scadenza (data)</Label>
              <Input type="date" data-testid="maint-next-date-input" value={mForm.next_due_date} onChange={(e) => setMForm({ ...mForm, next_due_date: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label>Prossima a {unit || "km/ore"}</Label>
              <Input type="number" value={mForm.next_due_km_hours} onChange={(e) => setMForm({ ...mForm, next_due_km_hours: e.target.value })} disabled={asset.tracking_type === "nessuno"} />
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <Label>Allegato fattura (PDF)</Label>
              <FileUploadField
                value={mForm.invoice_path}
                filename={mForm.invoice_filename}
                onChange={(path, filename) => setMForm({ ...mForm, invoice_path: path, invoice_filename: filename })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMOpen(false)}>Annulla</Button>
            <Button onClick={saveMaint} disabled={busy} data-testid="save-maintenance-button">{busy ? "Salvataggio…" : editMId ? "Aggiorna" : "Registra"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assign dialog with signature */}
      <Dialog open={aOpen} onOpenChange={setAOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">Assegna & presa in carico</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label>Dipendente assegnatario *</Label>
              <Select value={assignUser} onValueChange={setAssignUser}>
                <SelectTrigger data-testid="assign-user-select"><SelectValue placeholder="Seleziona dipendente" /></SelectTrigger>
                <SelectContent>
                  {employees.map((u) => <SelectItem key={u.id} value={u.id}>{u.name} — {u.department || u.email}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Data e ora di consegna *</Label>
              <Input type="datetime-local" data-testid="assign-date-input" value={assignDate} onChange={(e) => setAssignDate(e.target.value)} />
            </div>
            {asset.management_type === "quantita" && (
              <div className="space-y-1.5">
                <Label>Quantità da assegnare * <span className="text-muted-foreground font-normal">(disp. {asset.available_qty}/{asset.quantity})</span></Label>
                <Input type="number" min="1" max={asset.available_qty} data-testid="assign-qty-input" value={assignQty} onChange={(e) => setAssignQty(e.target.value)} />
              </div>
            )}
            <div className="space-y-1.5">
              <Label>Note (opzionale)</Label>
              <Input data-testid="assign-notes-input" value={assignNotes} onChange={(e) => setAssignNotes(e.target.value)} placeholder="Es. Consegnato con kit accessori" />
            </div>
            <div className="space-y-2 rounded-lg border border-border p-3">
              <label className="flex items-center gap-2 cursor-pointer">
                <Checkbox checked={skipSig} onCheckedChange={(v) => setSkipSig(!!v)} data-testid="skip-signature-checkbox" />
                <span className="text-sm font-medium">Salta firma (malfunzionamento dispositivo)</span>
              </label>
              {skipSig ? (
                <p className="text-xs text-muted-foreground">Il bene verrà assegnato senza firma. Potrai firmare da app in seguito o caricare il PDF firmato a mano dalla pagina Assegnazioni.</p>
              ) : (
                <div className="space-y-1.5 pt-1">
                  <Label>Firma dell'assegnatario (tavoletta grafica)</Label>
                  <p className="text-xs text-muted-foreground">Con la firma l'assegnatario dichiara di prendere in carico il bene.</p>
                  <SignaturePad ref={sigRef} label="Firma qui" />
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAOpen(false)}>Annulla</Button>
            <Button onClick={assign} disabled={busy} data-testid="confirm-assign-button">{busy ? "Salvataggio…" : skipSig ? "Assegna senza firma" : "Firma e assegna"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Return dialog with two signatures */}
      <Dialog open={rOpen} onOpenChange={setROpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">Riconsegna del bene</DialogTitle>
            <DialogDescription>Verbale di restituzione con doppia firma.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label>Operatore POWERCOM (chi effettua l'operazione) *</Label>
              <Input data-testid="return-operator-input" value={operatorName} onChange={(e) => setOperatorName(e.target.value)} placeholder="Nome e cognome" />
            </div>
            <div className="space-y-1.5">
              <Label>Data e ora di restituzione *</Label>
              <Input type="datetime-local" data-testid="return-date-input" value={returnDate} onChange={(e) => setReturnDate(e.target.value)} />
            </div>
            <label className="flex items-center gap-2 cursor-pointer rounded-lg border border-border p-3">
              <Checkbox checked={skipReturnSig} onCheckedChange={(v) => setSkipReturnSig(!!v)} data-testid="return-skip-signature-checkbox" />
              <span className="text-sm font-medium">Salta firma (malfunzionamento dispositivo)</span>
            </label>
            <div className="space-y-1.5">
              <Label>Verbale di restituzione firmato in olografo <span className="text-muted-foreground font-normal">(opzionale)</span></Label>
              <input ref={returnDocInputRef} type="file" accept="application/pdf" onChange={handleReturnSignedDoc} className="hidden" data-testid="return-signed-doc-input" />
              {returnSignedPath ? (
                <div className="flex items-center gap-2 rounded-lg border border-border p-2 text-sm" data-testid="return-signed-doc-info">
                  <FileCheck2 className="h-4 w-4 text-emerald-600" />
                  <span className="flex-1 truncate">{returnSignedName || "documento.pdf"}</span>
                  <Button type="button" variant="ghost" size="icon" onClick={() => { setReturnSignedPath(""); setReturnSignedName(""); }} data-testid="return-remove-signed-doc">
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </Button>
                </div>
              ) : (
                <Button type="button" variant="outline" size="sm" disabled={returnDocBusy}
                  onClick={() => returnDocInputRef.current?.click()} data-testid="return-upload-signed-doc">
                  {returnDocBusy ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Upload className="h-4 w-4 mr-1.5" />}
                  Carica PDF firmato
                </Button>
              )}
            </div>
            {!skipReturnSig && !returnSignedPath && (
              <>
                <div className="space-y-1.5">
                  <Label>Firma per POWERCOM</Label>
                  <SignaturePad ref={opSigRef} label="Firma operatore" />
                </div>
                <div className="space-y-1.5">
                  <Label>Firma dell'assegnatario che riconsegna</Label>
                  <SignaturePad ref={empSigRef} label="Firma assegnatario" />
                </div>
              </>
            )}
            <div className="space-y-1.5">
              <Label>Foto danni / stato del bene <span className="text-muted-foreground font-normal">(opzionale, allegate al verbale)</span></Label>
              <input ref={damageInputRef} type="file" accept="image/*" onChange={addDamagePhoto} className="hidden" data-testid="damage-photo-input" />
              <div className="flex items-center gap-3 flex-wrap">
                {damagePhotos.map((p, i) => (
                  <div key={i} className="relative h-20 w-20 rounded-md border border-border overflow-hidden">
                    <img src={fileUrl(p)} alt={`Danno ${i + 1}`} className="h-full w-full object-cover" />
                    <button type="button" onClick={() => setDamagePhotos((arr) => arr.filter((_, j) => j !== i))}
                      data-testid={`remove-damage-photo-${i}`}
                      className="absolute top-0.5 right-0.5 bg-background/90 rounded-full p-0.5 border border-border">
                      <X className="h-3.5 w-3.5 text-destructive" />
                    </button>
                  </div>
                ))}
                <Button type="button" variant="outline" size="sm" disabled={photoBusy}
                  onClick={() => damageInputRef.current?.click()} data-testid="add-damage-photo-button">
                  {photoBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImagePlus className="h-4 w-4 mr-1.5" />}
                  Aggiungi foto
                </Button>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setROpen(false)}>Annulla</Button>
            <Button onClick={doReturn} disabled={busy} data-testid="confirm-return-button">{busy ? "Salvataggio…" : "Conferma riconsegna"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {lightbox !== null && allDamage[lightbox] && (
        <div className="fixed inset-0 z-[60] bg-black/90 flex items-center justify-center" data-testid="damage-lightbox" onClick={() => setLightbox(null)}>
          <button type="button" onClick={() => setLightbox(null)} data-testid="lightbox-close"
            className="absolute top-4 right-4 text-white/80 hover:text-white p-2">
            <X className="h-7 w-7" />
          </button>
          {allDamage.length > 1 && (
            <button type="button" data-testid="lightbox-prev"
              onClick={(e) => { e.stopPropagation(); setLightbox((i) => (i - 1 + allDamage.length) % allDamage.length); }}
              className="absolute left-3 sm:left-6 text-white/80 hover:text-white p-2 rounded-full bg-white/10 hover:bg-white/20">
              <ChevronLeft className="h-7 w-7" />
            </button>
          )}
          <img src={fileUrl(allDamage[lightbox].path)} alt="Danno" onClick={(e) => e.stopPropagation()}
            className="max-h-[88vh] max-w-[90vw] object-contain rounded-md shadow-2xl" data-testid="lightbox-image" />
          {allDamage.length > 1 && (
            <button type="button" data-testid="lightbox-next"
              onClick={(e) => { e.stopPropagation(); setLightbox((i) => (i + 1) % allDamage.length); }}
              className="absolute right-3 sm:right-6 text-white/80 hover:text-white p-2 rounded-full bg-white/10 hover:bg-white/20">
              <ChevronRight className="h-7 w-7" />
            </button>
          )}
          <div className="absolute bottom-5 left-1/2 -translate-x-1/2 text-white/80 text-sm label-caps">
            {lightbox + 1} / {allDamage.length}
          </div>
        </div>
      )}
    </div>
  );
}
